import React, { useEffect, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { Redirect, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ApartmentSearchPanel } from './ApartmentSearchPanel';
import { AddressSearchPanel } from './AddressSearchPanel';
import { MapSearchPanel } from './MapSearchPanel';
import { useNewAnnouncement } from './api';
import { projectFromPlan } from './data';
import { FilterTab, Project, BannerStep, Announcement } from './types';
import AnnouncementHeader from './components/AnnouncementHeader';
import AnnouncementBanner from './components/AnnouncementBanner';
import ProjectGrid from './components/ProjectGrid';
import FilterTabs from './components/FilterTabs';
import AnnouncementFooter from './components/AnnouncementFooter';
import { projectRedux } from '../projects/project/ui/state/state';
import { IProject } from '../projects/project/data/entities';
import { ApartmentDetailInline } from './ApartmentDetailInline';
import { IAppReduxState } from '../../redux/store';
import { IAsyncData } from '../../core/models';
import { isLoading } from '../../core/redux';
import './SaleAnnouncement.scss';
import './ApartmentSearchPanel.scss';
import './MapSearchPanel.scss';


const AZ_MONTHS = [
  'YANVAR', 'FEVRAL', 'MART', 'APREL', 'MAY', 'İYUN',
  'İYUL', 'AVQUST', 'SENTYABR', 'OKTYABR', 'NOYABR', 'DEKABR',
];

/** Format the ISO date into Cover/1's card content: a `date` line
 *  ("29 İYUL") and a `time` line ("10:00"). Returns null when the
 *  input is missing or invalid so callers can fall back to placeholders. */
function formatBannerDateTime(iso?: string | null): { date: string; time: string } | null {
  if (!iso) return null;
  const d: Dayjs = dayjs(iso);
  if (!d.isValid()) return null;
  /* Build the hour off `d.hour()` (always 0-23) instead of `HH`, so
     midnight is rendered as "0:00" — not "12:00" or "00:00". */
  const hour = d.hour();
  return {
    /* `D`, not `DD` — the day is shown unpadded ("4 SENTYABR", not
       "04 SENTYABR"). */
    date: `${d.format('D')} ${AZ_MONTHS[d.month()]}`,
    time: `${hour}:${d.format('mm')}`,
  };
}

/** Build the three banner steps from an announcement.
 *
 *  Cover/1 dropped the dedicated "Elan tarixi" card — the start of the
 *  dashed track is now the publication moment implicitly. So we keep
 *  only the three milestones from `starts_on`, `wait_starts_on`,
 *  `ends_on`. The "active" step is still the latest one whose date
 *  is at or before now. */
function buildBannerSteps(a: Announcement | null): BannerStep[] {
  const slots: { label: string; iso: string | null | undefined }[] = [
    { label: 'Satışın başlama tarixi',           iso: a?.starts_on },
    { label: 'Gözləmə qaydasında mənzil seçimi tarixi', iso: a?.wait_starts_on },
    { label: 'Satışın bitmə tarixi',   iso: a?.ends_on },
  ];

  const now = dayjs();
  let activeIdx = 0;
  slots.forEach((s, i) => {
    if (!s.iso) return;
    const d = dayjs(s.iso);
    if (d.isValid() && !d.isAfter(now)) activeIdx = i;
  });

  return slots.map((s, i) => {
    const dt = formatBannerDateTime(s.iso) ?? { date: '—', time: '—' };
    return {
      label: s.label,
      date: dt.date,
      time: dt.time,
      active: i === activeIdx,
    };
  });
}

const PLACEHOLDER_BANNER_STEPS: BannerStep[] = [
  { label: 'Satışın başlama tarixi',           date: '—', time: '—', active: true  },
  { label: 'Gözləmə qaydası tarixi', date: '—', time: '—', active: false },
  { label: 'Satışın bitmə tarixi',   date: '—', time: '—', active: false },
];


interface SaleAnnouncementProps {
  /** Private preview (unlisted route): never redirects home. Shows the
   *  `is_new` announcement, else the latest one, or the one in `:id`. */
  preview?: boolean;
}

const SaleAnnouncement: React.FC<SaleAnnouncementProps> = ({ preview = false }) => {
  /* The public route carries no id — the page resolves the announcement
     flagged `is_new` from the API itself, and redirects home when there
     isn't one. */
  const { id: previewId } = useParams<{ id?: string }>();
  const { data: announcement, loading, error, noneFlagged } = useNewAnnouncement({
    fallbackToLatest: preview,
    id: preview ? previewId : undefined,
  });

  /* Surface the announcement state in the console so the data flow is
     visible while wiring this up against the live API. */
  useEffect(() => {
    console.log('[SaleAnnouncement] announcement state:', { loading, error, announcement });
  }, [announcement, loading, error]);

  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<FilterTab>('unvan');
  /* When the user clicks a row in the parameter / address search results,
     we swap the filter section for <ApartmentDetailPage>. Clearing this
     restores the search panels. */
  const [selectedApartmentId, setSelectedApartmentId] = useState<string | null>(null);

  /* Clear any open apartment detail when the user switches project,
     announcement, or tab — otherwise the detail page would stay open
     while the search panels underneath have already navigated away. */
  useEffect(() => {
    setSelectedApartmentId(null);
  }, [selectedProjectId, announcement?.id, activeTab]);

  /* Map the API's `project_plans` to the UI `Project` shape used by
     ProjectCard / search panels. Cash + mortgage tags come from each
     plan's own flags (not the announcement). When data hasn't loaded
     yet we render no cards rather than a hard-coded fallback.

     Sort by total apartment count (sum of room buckets) descending,
     so the project with the most apartments leads the "Layihə
     seçimi" row. */
  const projects: Project[] = useMemo(() => {
    if (!announcement?.project_plans) return [];
    const mapped = announcement.project_plans.map((plan) =>
      projectFromPlan(plan, 'az'),
    );
    const total = (p: Project) =>
      (p.rooms[0] ?? 0) +
      (p.rooms[1] ?? 0) +
      (p.rooms[2] ?? 0) +
      (p.rooms[3] ?? 0) +
      (p.rooms[4] ?? 0);
    return [...mapped].sort((a, b) => total(b) - total(a));
  }, [announcement]);

  /* Pick the first plan automatically when the list arrives and the
     user hasn't selected anything yet (or selected a plan that's no
     longer in the list — happens if the announcement id changes). */
  useEffect(() => {
    if (projects.length === 0) return;
    if (selectedProjectId == null || !projects.some((p) => p.id === selectedProjectId)) {
      setSelectedProjectId(projects[0].id);
    }
  }, [projects, selectedProjectId]);

  const selectedProject = useMemo(
    () => projects.find((p) => p.id === selectedProjectId) ?? projects[0],
    [projects, selectedProjectId],
  );

  /* Pull the real project detail (image + sectors + floorplans, etc.)
     from the existing project Redux slice — same pattern as
     `ProjectDetailPage` in /layihe/:id. */
  const dispatch = useDispatch();
  useEffect(() => {
    if (selectedProjectId == null) return;
    console.log('[SaleAnnouncement] fetching project detail for id =', selectedProjectId);
    dispatch(projectRedux.actions.getDetail(String(selectedProjectId)));
  }, [dispatch, selectedProjectId]);

  const projectDetailBranch = useSelector<IAppReduxState, IAsyncData<IProject>>(
    (s) => s.project.details,
  );

  const sectors = projectDetailBranch.data?.sectors;
  const projectImage = projectDetailBranch.data?.image;
  const projectLoading = isLoading(projectDetailBranch);
  useEffect(() => {
    console.log('[SaleAnnouncement] project detail:', {
      loading: projectLoading,
      sectorsCount: sectors?.length ?? 0,
      hasImage: Boolean(projectImage),
    });
  }, [projectLoading, sectors, projectImage]);

  // const bannerTitle = loading
  //   ? 'Satış elanı yüklənir...'
  //   : announcement
  //     ? `Satış elanı № ${announcement.id}`
  //     : ' ';

  const bannerTitle = '';

  const bannerSteps: BannerStep[] = useMemo(() => {
    if (!announcement) return PLACEHOLDER_BANNER_STEPS;
    return buildBannerSteps(announcement);
  }, [announcement]);

  /* Everything below runs after every hook, so the hook order stays stable
     across renders.

     Render nothing at all until the announcement is resolved. Showing the
     placeholder banner while the request is in flight would flash a glimpse
     of a page the visitor is about to be redirected away from — and we can't
     know whether an `is_new` announcement exists until the list comes back. */
  if (loading) return null;

  /* Nothing is flagged `is_new` — send the visitor home rather than render an
     empty banner and project grid. */
  if (noneFlagged) {
    console.log('[SaleAnnouncement] no is_new announcement — redirecting to /');
    return <Redirect to="/" />;
  }

  /* Request failed, or resolved to nothing renderable — stay blank rather
     than show a hollow shell. */
  if (!announcement) return null;

  return (
    <div className="sale-announcement">
      <AnnouncementHeader />
      <AnnouncementBanner title={bannerTitle} steps={bannerSteps} loading={loading && !announcement} />

      <ProjectGrid
        projects={projects}
        selectedId={selectedProjectId}
        onSelect={setSelectedProjectId}
      />


      <section className="sa-filter">
        {/* Filter tabs are always visible — they let the user switch
            between Ünvan / Xəritə / Parametrlər at any point, even
            while an apartment-detail view is open. */}
        <FilterTabs activeTab={activeTab} onChange={setActiveTab} />

        {selectedApartmentId && selectedProject && (
          <ApartmentDetailInline
            apartmentId={selectedApartmentId}
            activePlan={announcement?.project_plans.find(
              (p) => p.id === selectedProject.id,
            )}
            projectImage={projectImage}
            onBack={() => setSelectedApartmentId(null)}
          />
        )}

        {/* Tab panels stay mounted (hidden via CSS) while the detail
            view is open so MapSearchPanel / etc. keep their internal
            step + selection state. Geri then returns exactly one step
            back instead of resetting to step 1. */}
        {selectedProject && (
          <div style={selectedApartmentId ? { display: 'none', width: '100%' } : { width: '100%' }}>
            {activeTab === 'parametrler' && (
              <ApartmentSearchPanel
                projectPlanId={selectedProject.id}
                announcementId={announcement?.id}
                sectors={sectors}
                projectName={selectedProject.name}
                onApartmentClick={(apt) =>
                  setSelectedApartmentId(String(apt.id))
                }
              />
            )}
            {activeTab === 'xerite' && (
              <MapSearchPanel
                projectPlanId={selectedProject.id}
                announcementId={announcement?.id}
                sectors={sectors}
                image={projectImage}
                activePlan={announcement?.project_plans.find(
                  (p) => p.id === selectedProject.id,
                )}
                onApartmentClick={(apt) =>
                  setSelectedApartmentId(String(apt.id))
                }
              />
            )}
            {activeTab === 'unvan' && (
              <AddressSearchPanel
                projectPlanId={selectedProject.id}
                announcementId={announcement?.id}
                projectName={selectedProject.name}
                onApartmentClick={(apt) =>
                  setSelectedApartmentId(String(apt.id))
                }
              />
            )}
          </div>
        )}
      </section>
      <AnnouncementFooter />
    </div>
  );
};

export default SaleAnnouncement;